import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, DestroyRef, HostBinding } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.css'],
})
export class DatePickerComponent implements OnInit {
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  @ViewChild('datePicker') datePicker!: MatDatepicker<Date>;

  @Input({ required: true }) dateFormControl: AbstractControl;
  @Input({ required: true }) label: string = '';
  @Input() placeholder: string = 'dd/mm/aaaa';
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;
  @Input() class: string = '';
  @Input() showToggle: boolean = true;
  @Input() togglePosition: 'prefix' | 'suffix' = 'suffix';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;

  // Outputs para eventos (opcionais)
  @Output() dateChange = new EventEmitter<string>();
  @Output() dateInput = new EventEmitter<string>();

  // HostBinding para aplicar as classes no elemento host
  @HostBinding('class')
  get hostClasses() {
    return this.class;
  }

  constructor(private destroyRef: DestroyRef) {}

  ngOnInit(): void {
    // Escuta mudanças no FormControl para sincronizar o input
    this.dateFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.updateInputFromControlValue(value);
    });

    // Se já existe um valor inicial, atualiza o input
    if (this.dateFormControl.value) {
      this.updateInputFromControlValue(this.dateFormControl.value);
    }

    // Adiciona os validadores customizados se necessário
    this.setupCustomValidators();
  }

  private setupCustomValidators(): void {
    // Adiciona validadores customizados ao FormControl
    const existingValidators = this.dateFormControl.validator;

    this.dateFormControl.setValidators([...(existingValidators ? [existingValidators] : []), this.dateValidator.bind(this)]);

    this.dateFormControl.updateValueAndValidity();
  }

  private dateValidator(control: FormControl): any {
    const value = control.value;

    if (!value || typeof value !== 'string' || value.trim() === '') {
      return null; // Campo vazio é válido (required será tratado pelo Angular)
    }

    // Remove caracteres não numéricos para validação
    const cleanValue = value.replace(/[^\d]/g, '');

    // Se tem algum dígito mas não está completo (deve ter 8 dígitos: ddmmaaaa)
    if (cleanValue.length > 0 && cleanValue.length < 8) {
      return { incompleteDate: { message: 'Data incompleta' } };
    }

    // Validação dos componentes da data (dia, mês, ano)
    if (cleanValue.length >= 2) {
      const day = parseInt(cleanValue.substring(0, 2), 10);
      if (day < 1 || day > 31) {
        return { invalidDate: { message: 'Dia inválido' } };
      }
    }

    if (cleanValue.length >= 4) {
      const month = parseInt(cleanValue.substring(2, 4), 10);
      if (month < 1 || month > 12) {
        return { invalidDate: { message: 'Mês inválido' } };
      }
    }

    if (cleanValue.length >= 6) {
      const year = parseInt(cleanValue.substring(4, cleanValue.length), 10);
      if (cleanValue.length < 8 && year < 1900) {
        return { invalidDate: { message: 'Ano inválido' } };
      }
    }

    // Se tem 8 dígitos, verifica se a data é válida como um todo
    if (cleanValue.length === 8) {
      const date = this.parseStringToDate(value);
      if (!date) {
        return { invalidDate: { message: 'Data inválida' } };
      }

      // Verifica limites min/max se definidos
      if (this.min && date < this.min) {
        return { minDate: { message: `Data deve ser maior que ${this.formatDateToString(this.min)}` } };
      }

      if (this.max && date > this.max) {
        return { maxDate: { message: `Data deve ser menor que ${this.formatDateToString(this.max)}` } };
      }
    }

    return null; // Válido
  }

  private updateInputFromControlValue(value: any): void {
    let formattedValue = '';

    if (value) {
      if (typeof value === 'string') {
        formattedValue = this.isFormattedDate(value) ? value : this.formatInputValue(value);
      } else if (value instanceof Date) {
        formattedValue = this.formatDateToString(value);
      }
    }

    // Atualiza o input sem disparar eventos para evitar loop
    setTimeout(() => {
      if (this.inputElement?.nativeElement) {
        this.inputElement.nativeElement.value = formattedValue;
      }
    }, 0);
  }

  // Métodos de formatação e conversão
  private formatDateToString(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private parseStringToDate(dateString: string): Date | null {
    if (!dateString) return null;

    const cleanDate = dateString.replace(/[^\d]/g, '');

    if (cleanDate.length !== 8) return null;

    const day = parseInt(cleanDate.substring(0, 2), 10);
    const month = parseInt(cleanDate.substring(2, 4), 10) - 1;
    const year = parseInt(cleanDate.substring(4, 8), 10);

    const date = new Date(year, month, day);

    // Verifica se a data é válida
    if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
      return null;
    }

    return date;
  }

  private isFormattedDate(value: string): boolean {
    return /^\d{2}\/\d{2}\/\d{4}$/.test(value);
  }

  private formatInputValue(value: string): string {
    const cleanValue = value.replace(/[^\d]/g, '');

    let formatted = '';

    if (cleanValue.length >= 1) {
      formatted += cleanValue.substring(0, 2);
    }
    if (cleanValue.length >= 3) {
      formatted += '/' + cleanValue.substring(2, 4);
    }
    if (cleanValue.length >= 5) {
      formatted += '/' + cleanValue.substring(4, 8);
    }

    return formatted;
  }

  // Event handlers
  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const rawValue = target.value;

    // Formata o valor enquanto digita
    const formattedValue = this.formatInputValue(rawValue);

    // Atualiza o input com o valor formatado
    target.value = formattedValue;

    // Atualiza o FormControl diretamente
    this.dateFormControl.setValue(formattedValue);
    this.dateFormControl.markAsDirty();

    // Emite eventos opcionais
    this.dateInput.emit(formattedValue);
  }

  onInputBlur(event: FocusEvent): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // Marca como touched para ativar validações
    this.dateFormControl.markAsTouched();

    // Garante que o valor está sincronizado
    if (this.dateFormControl.value !== value) {
      this.dateFormControl.setValue(value);
    }

    this.dateChange.emit(value);
  }

  onInputFocus(event: FocusEvent): void {
    const target = event.target as HTMLInputElement;
    setTimeout(() => target.select(), 0);
  }

  onInputClick(event: MouseEvent): void {
    const target = event.target as HTMLInputElement;
    target.select();
  }

  onDatePickerChange(event: any): void {
    const selectedDate = event.value as Date;

    if (selectedDate) {
      const formattedDate = this.formatDateToString(selectedDate);

      // Atualiza o FormControl
      this.dateFormControl.setValue(formattedDate);
      this.dateFormControl.markAsDirty();
      this.dateFormControl.markAsTouched();

      // Atualiza o input
      this.updateInputFromControlValue(formattedDate);

      this.dateChange.emit(formattedDate);
    }
  }

  // Métodos públicos de conveniência
  getFormattedValue(): string {
    return this.dateFormControl.value || '';
  }

  getDateValue(): Date | null {
    return this.parseStringToDate(this.dateFormControl.value);
  }

  isValidDate(): boolean {
    const value = this.dateFormControl.value;
    if (!value) return true;
    return this.parseStringToDate(value) !== null;
  }

  // Getter para facilitar o uso no template
  get formControl(): FormControl {
    return this.dateFormControl as FormControl;
  }
}
